import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useState, useEffect } from "react";
import api from "../../../services/api";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import toastOptions from "../../../utils/toastOptions";
import {
  calcYearsOld,
  CEPMask,
  dateMask,
  displayDate,
  displayDateTime,
  handleFocus,
  numberMask,
  phoneMask,
} from "../../../utils/masks";
import { Gender } from "../../../models/enums/Gender";
import { useNavigate, useParams } from "react-router-dom";
import { getValidator } from "../../../utils/validatorsYup";

import { authenticationService } from "../../../services/auth.service";

import "../../shared/modal.css";
import "./youth-person-edit.css";
import "../../shared/button.css";
import { StatusType } from "../../../models/enums/StatusType";
import IPersonDTO from "../../../models/IPersonDTO";
import Param from "../../../models/Param";

import states from '../../../assets/br.citys.json';

type RouteParams = {
  id: string;
}

export default function YouthPersonAddEdit() {
  const params = useParams<RouteParams>();
  const [person, setPerson] = useState<IPersonDTO>();
  const [isDisabled, setDisabled] = useState<boolean>(true);
  const [canChange, setCanChange] = useState<boolean>(false);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [congregations, setCongregations] = useState<Param[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [zipCodeSearching, setZipCodeSearching] = useState(false);

  const navigate = useNavigate();

  const schema = yup.object().shape({
    name: yup
      .string()
      .required("Nome obrigatório")
      .max(100, (val) => `Máximo ${val.max} caracteres`)
      .min(3, (val) => `Mínimo ${val.min} caracteres`),
    gender: yup.string().required("Gênero obrigatório"),
    phone: yup.string().required(`Telefone é obrigatório`),
    email: yup.string().required(`E-mail é obrigatório`).max(100, (val) => `Máximo ${val.max} caracteres`),
    birthDate: getValidator({ isDate: true }).required(
      "Nascimento obrigatório"
    ),
    note: yup.string()
      .max(250, (val) => `Máximo ${val.max} caracteres`),
    idCongregation: getValidator({isNumeric: true}).required('Congregação obrigatória'),
    address: yup.object().shape({
      zipCode: yup.string(),
      address: yup.string()
        .max(100, (val) => `Máximo ${val.max} caracteres`),
      state: yup.string(),
      city: yup.string(),
      neighborhood: yup.string()
        .max(50, (val) => `Máximo ${val.max} caracteres`),
      numberAddress: getValidator({isNumeric: true}),
      addressLine2: yup.string()
        .max(100, (val) => `Máximo ${val.max} caracteres`)
    })
  });

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    errors,
    formState
  } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    
    init();
  }, [params.id]);

  const init = async () => {
    const dataCongregations = await api.get('congregations/dashboard');
    setCongregations(dataCongregations.data);

    const id = +(params.id ?? 0);

    const adding = id ? id <= 0 : true;

    if (!adding) {
      handleEdit(id);
    }

    setDisabled(
      (authenticationService.isAuxSec() && !adding) ||
        authenticationService.isReadOnly()
    );

    setCanChange(authenticationService.canChangeYouth());
  };

  const handleEdit = async (id: number) => {
    try {
      const { data } = await api.get(`/youths/${id}`);

      if (data.item.address) {
        selectCities(data.item.address.state);
      }

      setPerson(data.item);

    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.warning(m, toastOptions);
      });
    }
  };

  const onSubmit = async (data: IPersonDTO) => {
    try {
      data.id = person?.id ?? 0;

      if (!data?.id) {
        const result = await api.post('youths', data);
        toast.success('Jovem cadastrado com sucesso!', toastOptions);

        navigate(`/jovens/${result.data.item.id}`);
      } else {
        const { data: result } = await api.put('youths', data);
        toast.success('Jovem atualizado com sucesso!', toastOptions);

        const m = Object.assign(data, {});
        result.item.item.id = 0;
        result.item.item.id = m.id;
        setPerson(result.item.item);
      }
      
      setRefresh(!refresh);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    }
  };

  const handleCEP = (value: string) => {
    const cep = value;

    if (cep.length == 9) {
      setZipCodeSearching(true);
      api.get(`https://viacep.com.br/ws/${cep}/json/`)
      .finally(()=> setZipCodeSearching(false))
      .then((resp: any) => {
        const { data } = resp;
        if (!data.erro) {
          setValue('address.neighborhood', data.bairro);
          setValue('address.address', data.logradouro);
          setValue('address.state', data.uf);
          selectCities(data.uf);
          setValue('address.city', data.localidade);

          setPerson({
            ...person,
            address: {
              neighborhood: data.bairro,
              address: data.logradouro,
              state: data.uf,
              city: data.localidade
            }
          } as IPersonDTO);                              
        }
        
        if (data.logradouro) {                        
          document.getElementById('numberAddress')?.focus();
        } else {
          document.getElementById('address')?.focus();
        }
      });
    }

    setValue('address.zipCode', cep)

    return CEPMask(value);
  }

  const selectCities = (state: string) => {
    const citiesFromState = states.filter(s => s.initial === state).shift()?.cities;
    if (citiesFromState) {
      setCities(citiesFromState);
    }
  }

  return (params.id && !person) ? (
    <div className="uk-flex uk-flex-center">
      <div uk-spinner="true; ratio:2">Carregando...</div>
    </div>
  ) : (
    <div id="page-edit-person-event" className="uk-section uk-section-default uk-padding-small">     
      <form
        onSubmit={handleSubmit<IPersonDTO>(onSubmit)}
        onReset={reset}
      >
        <div className="uk-modal-body">
          <div className="uk-grid-small" uk-grid="true">
            <div className="uk-width-3-4@s">
              <label className="uk-form-label" htmlFor="name">Nome</label>
              <input
                type="text"
                id="name"
                name="name"
                placeholder="Nome"
                className="uk-input uk-form-small"
                defaultValue={person?.name}
                style={{ textTransform: "uppercase" }}
                autoComplete={"off"}
                onFocus={handleFocus}
                ref={register}
              />
              <small className="uk-text-danger">{errors.name?.message}</small>
            </div>
            <div className="uk-width-1-4@s">
              <label className="uk-form-label" htmlFor="gender">Sexo</label>
              <div
                uk-form-custom="target: > * > span:first-child"
                className="uk-width-1"
              >
                <select
                  className="uk-form-small"
                  id="gender"
                  name="gender"
                  defaultValue={person?.gender}
                  ref={register}
                >
                  <option value="" key={`gender0`}>Selecione...</option>
                  <option value={Gender.FEMALE} key={`gender1`}>Feminino</option>
                  <option value={Gender.MALE} key={`gender2`}>Masculino</option>
                </select>
                <button
                  className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                  style={{ textTransform: "none" }}
                  type="button"
                  tabIndex={-1}
                >
                  <span></span>
                  <span uk-icon="icon: chevron-down"></span>
                </button>
              </div>
              <small className="uk-text-danger">
                {errors.gender?.message}
              </small>
            </div>            
            <div className="uk-width-3-4@s">
              <label className="uk-form-label" htmlFor="email">E-mail</label>
              <input
                type="text"
                id="email"
                name="email"
                placeholder="email@email.com"
                className="uk-input uk-form-small"
                defaultValue={person?.email}
                autoComplete={"off"}
                ref={register}
              />
              <small className="uk-text-danger">
                {errors.email?.message}
              </small>
            </div>
            <div className="uk-width-1-4@s">
              <label className="uk-form-label" htmlFor="birthDate">
                Nascimento {calcYearsOld(person?.birthDate?.toString())}
              </label>
              <input
                type="type"
                id="birthDate"
                name="birthDate"
                placeholder="99/99/9999"
                className="uk-input uk-form-small"
                defaultValue={displayDate(person?.birthDate?.toString())}
                autoComplete={"off"}
                onFocus={handleFocus}
                ref={register}
                onChange={(e) => {
                  e.target.value = dateMask(e.target.value);
                }}
              />
              <small className="uk-text-danger">
                {errors.birthDate?.message}
              </small>
            </div>
            <div className="uk-width-1-4@m">
              <label className="uk-form-label" htmlFor="phone">Celular</label>
              <input type="text" 
                id="phone" 
                name="phone" 
                placeholder="(99) 99999-9999" 
                className="uk-input uk-form-small"
                defaultValue={phoneMask(person?.phone ?? '')}
                ref={register}
                autoComplete={"off"}
                onChange={(e) => { e.target.value = phoneMask(e.target.value) }}
              />
              <small className="uk-text-danger">{errors.phone?.message}</small>
            </div>
            <div className="uk-width-1-3@m">
              <label className="uk-form-label" htmlFor="idCongregation">Congregação</label>
              <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                <select className="uk-form-small" id="idCongregation" 
                  name="idCongregation"
                  defaultValue={person?.idCongregation}
                  ref={register}
                >
                  <option value="" key={`congregation0`}>Selecione...</option>
                  {
                    congregations.map(c => {
                      return <option value={c.id} key={`congregation${c.id}`}>{c.description}</option>
                    })
                  }
                </select>
                <button 
                  className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                  style={{textTransform: 'none'}} 
                  type="button" 
                  tabIndex={-1}
                >
                  <span></span>
                  <span uk-icon="icon: chevron-down"></span>
                </button>
              </div>
              <small className="uk-text-danger">{errors.idCongregation?.message}</small>
            </div>
          </div>
          <div className="uk-grid-small" uk-grid="true">
            <div className="uk-width-1-2@s">
              <div className="uk-grid-small" uk-grid="true">
                <div className="uk-width-1-4@s">
                  <label className="uk-form-label" htmlFor="zipCode">CEP</label>
                  <input type="text" 
                    id="zipCode" 
                    name="address.zipCode" 
                    placeholder="00000-000" 
                    className="uk-input uk-form-small" 
                    defaultValue={person?.address?.zipCode}
                    autoComplete={"off"}
                    ref={register}
                    onFocus={handleFocus}
                    onChange={(e) => { e.target.value = handleCEP(e.target.value) }}
                    disabled={zipCodeSearching}
                  />
                  <small className="uk-text-danger">{errors.zipCode?.message}</small>
                </div> 
                <div className="uk-width-3-4@s">
                  <label className="uk-form-label" htmlFor="address">Endereço</label>
                  <input type="text" 
                    id="address" 
                    name="address.address" 
                    placeholder="Endereço" 
                    className="uk-input uk-form-small" 
                    defaultValue={person?.address?.address}
                    style={{textTransform: 'uppercase'}}
                    autoComplete={"off"}
                    ref={register}
                  />
                  <small className="uk-text-danger">{errors.address?.message}</small>
                </div> 
              </div>
              <div className="uk-grid-small" uk-grid="true">
                <div className="uk-width-1-4@s">
                  <label className="uk-form-label" htmlFor="numberAddress">Número</label>
                  <input type="text" 
                    id="numberAddress" 
                    name="address.numberAddress"
                    placeholder="00000" 
                    className="uk-input uk-form-small"
                    defaultValue={person?.address?.numberAddress}
                    ref={register}
                    autoComplete={"off"}
                    onFocus={handleFocus}
                    onChange={(e) => { e.target.value = numberMask(e.target.value) }}
                  />
                  <small className="uk-text-danger">{errors.numberAddress?.message}</small>
                </div> 
                <div className="uk-width-3-4@m">
                  <label className="uk-form-label" htmlFor="neighborhood">Bairro</label>
                  <input type="text" 
                    id="neighborhood" 
                    name="address.neighborhood" 
                    placeholder="Bairro" 
                    className="uk-input uk-form-small" 
                    defaultValue={person?.address?.neighborhood}
                    style={{textTransform: 'uppercase'}}
                    autoComplete={"off"}
                    ref={register}
                  />
                  <small className="uk-text-danger">{errors.address?.neighborhood?.message}</small>
                </div>
                <div className="uk-width-1-2@m">
                  <label className="uk-form-label" htmlFor="address.state">Estado</label>
                  <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                    <select className="uk-form-small uk-select"
                      id="address.state" 
                      name="address.state"
                      defaultValue={person?.address?.state}
                      ref={register}
                      onChange={(el) => selectCities(el.target.value)}
                    >
                      <option value="" key={`state0`}>Selecione...</option>
                      {
                        states.map(s => {
                          return <option value={s.initial} key={`state${s.initial}`}>{s.name}</option>
                        })
                      }
                    </select>
                    <button 
                      className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                      style={{textTransform: 'none'}} 
                      type="button" 
                      tabIndex={-1}
                    >
                      <span>{states.filter(s => s.initial == person?.address?.state).shift()?.name}</span>
                      <span uk-icon="icon: chevron-down"></span>
                    </button>
                  </div>
                  <small className="uk-text-danger">{errors.address?.state?.message}</small>
                </div>
                <div className="uk-width-1-2@m">
                  <label className="uk-form-label" htmlFor="city">Cidade</label>
                  <div uk-form-custom="target: > * > span:first-child" className="uk-width-1">
                    <select className="uk-form-small"
                      id="city" 
                      name="address.city"
                      defaultValue={person?.address?.city}
                      ref={register}
                      disabled={cities.length === 0}
                    >
                      <option value="" key={`city0`}>Selecione...</option>
                      {
                        cities.map(c => {
                          return <option value={c} key={`city${c}`}>{c}</option>
                        })
                      }
                    </select>
                    <button 
                      className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                      style={{textTransform: 'none'}} 
                      type="button" 
                      tabIndex={-1}
                    >
                      <span></span>
                      <span uk-icon="icon: chevron-down"></span>
                    </button>
                  </div>
                  <small className="uk-text-danger">{errors.address?.city?.message}</small>
                </div>
              </div>
              <div className="uk-grid-small" uk-grid="true">
                <div className="uk-width-1-1@s">
                  <label className="uk-form-label" htmlFor="addressLine2">Complemento</label>
                  <input type="text" 
                    id="addressLine2" 
                    name="address.addressLine2" 
                    placeholder="Complemento" 
                    className="uk-input uk-form-small" 
                    defaultValue={person?.address?.addressLine2}
                    style={{textTransform: 'uppercase'}}
                    autoComplete={"off"}
                    ref={register}
                  />
                  <small className="uk-text-danger">{errors.address?.addressLine2?.message}</small>
                </div>                           
              </div>                          
            </div>
            <div className="uk-width-1-2@s">
              <label className="uk-form-label" htmlFor="note">Observações</label>
              <textarea
                id="note" 
                name="note"
                className="uk-textarea uk-form-small" 
                rows={10} 
                style={{resize: 'none', textTransform: 'uppercase'}}
                defaultValue={person?.note}
                autoComplete={"off"}
                ref={register}
              />
              <small className="uk-text-danger">{errors.note?.message}</small>
            </div>
          </div>
          {person?.id && canChange && (
            <div className="uk-grid-small" uk-grid="true">
              <div className="uk-width-1-2@s">
                <span className="uk-text-italic uk-text-small">
                  Adicionado por:
                </span>
                <span className="uk-text-muted uk-text-small uk-margin-small-left">
                  {`${person?.createdBy} - ${displayDateTime(person?.createdDate)}`}
                </span>
              </div>
              {person.modifiedBy && (
                <div className="uk-width-1-2@s">
                  <span className="uk-text-italic uk-text-small">
                    Alterado por:
                  </span>
                  <span className="uk-text-muted uk-text-small uk-margin-small-left">
                    {`${person?.modifiedBy} - ${displayDateTime(person?.modifiedDate)}`}
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="uk-modal-footer uk-margin-bottom">
          <div className="uk-grid-small uk-flex uk-flex-center" uk-grid="true">
            <div className="uk-text-center">
              <button className="uk-button uk-button-default uk-button-small" onClick={()=> navigate('/jovens')}>Voltar</button>
            </div>
            {person?.status != StatusType.INACTIVE && (
              <>
                <div className="uk-text-center">
                  <button
                    id="btn-save"
                    disabled={
                      formState.isSubmitting ||
                      isDisabled
                    }
                    className="uk-button uk-button-primary uk-button-small uk-margin-left uk-width-1-1"
                    type="submit"
                  >
                    {formState.isSubmitting && <div uk-spinner="true; ratio:0.5"></div>}
                    Salvar
                  </button>
                </div>                
              </>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}
